/**
 * Which keys the command bar's phase renders (#505 slice 10, D34 to D41).
 *
 * `droneCommandAvailability` answers "may this verb be pressed, and if not
 * why not". This answers the question before it: does this verb belong on the
 * bar at all right now. The two are deliberately separate, because D13 turns on
 * the distinction between them - a verb the phase can NEVER accept is removed,
 * and a verb the phase owns but something is momentarily blocking stays, greyed,
 * carrying its refusal. That refusal is the product; this filter must not eat
 * it.
 *
 * Pure and its own module, like `landing-state.ts` beside it, so the rule is
 * testable without a render.
 */

import { isAirborne, isPositivelyOnGround } from './drone-command-availability';
import type { DroneCommandAction } from '@/hooks/useDroneCommand';
import type { LiveDrone } from '@/hooks/useWebSocket';

/**
 * The bar's keys, including the one that is not a command.
 *
 * MANUAL is a mode toggle rather than a one-shot verb: it takes an exclusive
 * authority lock instead of publishing a command. It still occupies a fixed
 * position in the key order, so it has to be nameable here.
 */
export type BarKeyAction = DroneCommandAction | 'manual';

export interface CommandKeyPhaseInputs {
  drone: LiveDrone;
  /** Fly-here pick mode is armed and waiting for a map click. */
  gotoArmed: boolean;
  /** The verb the companion app is holding until an ARM, or null. */
  heldAwaitingArm: DroneCommandAction | null;
  /** Whether this verb's own command is somewhere in its ack lifecycle. */
  isInFlight: (action: DroneCommandAction) => boolean;
}

/**
 * Does this key belong on the bar in the aircraft's current phase?
 *
 * **Fails open (D34).** A key is removed only on positive evidence that the
 * phase cannot accept it. Absent `is_flying`, absent `armed`, a mission fetch
 * that has not landed: every one of them renders the key, exactly as the bar
 * does today. That is the rule this codebase already applies wherever a
 * tri-state reaches the UI - `commandRouteOf`, `commandSecretPresentOf` and
 * `isAirborne` each say in their own words that absence is never an answer -
 * and the failure modes here are not symmetric. A key that renders needlessly
 * costs an operator one grey rectangle. A key that is absent when it was needed
 * costs them the verb, with nothing on screen to say it ever existed.
 *
 * Per-verb predicates rather than one `phase` enum, deliberately: an enum has to
 * assign the unknown cases somewhere, and every assignment of them is a guess
 * about an aircraft.
 */
export function isCommandKeyVisible(
  action: BarKeyAction,
  { drone, gotoArmed, heldAwaitingArm, isInFlight }: CommandKeyPhaseInputs,
): boolean {
  // D14 and D39. RTH and ABORT are rendered in every phase, in the same two
  // positions behind the red divider, whatever the aircraft is doing. An
  // operator reaching for RTH is not reading the bar, they are reaching for a
  // position their hand knows, and a key that is sometimes there is worse than
  // no key. MANUAL joins them: it is a mode toggle that already carries its own
  // refusal, and handing an aircraft to the sticks is a recovery path off a
  // mission going wrong.
  if (action === 'return_home' || action === 'abort' || action === 'manual') return true;

  // D39's other three exemptions. Each is the same defect in a different
  // costume: a control removed from under the interaction that is using it.
  // A key that vanishes mid-ack takes its own lifecycle readout with it; a
  // GO-TO removed while pick mode is armed leaves the map waiting for a click
  // with nothing on screen able to cancel it; and the verb the companion app is
  // holding is precisely the one the ARM prompt is pointing at.
  if (isInFlight(action)) return true;
  if (action === heldAwaitingArm) return true;
  if (action === 'goto' && gotoArmed) return true;

  // TWO predicates, not one and its negation (D45). `!isAirborne` is the
  // ABSENCE of a flight signal, which for the whole MAVLink fleet is nothing but
  // `altitude <= 2` - so using it to remove a verb removes it from an aircraft
  // that is one metre up and still flying. `isPositivelyOnGround` needs the
  // aircraft to SAY it is down. This codebase states the distinction in three
  // separate places; `mission-launch-recovery.ts` puts it best: "NOT AIRBORNE IS
  // NOT THE SAME AS ON THE GROUND ... the reading must CONTAIN a ground signal,
  // not merely lack a flight one."
  const airborne = isAirborne(drone);
  const onGround = isPositivelyOnGround(drone);

  switch (action) {
    // D35. Removed only when the aircraft is positively airborne, and NOT on
    // the ground: `HELD_ON_ARM` in DroneCommandBar records that
    // MissionController.kt routes takeoff through `if (!_armed)
    // holdMotorCommand(env)`, so pressing TAKEOFF on a disarmed companion
    // aircraft is the documented way to stage a launch - the app holds the
    // motor command and the COP's ARM key becomes its release. The plan's
    // illustrative phase-1 table lists no TAKEOFF; it was written before that
    // hold was read, and following it would delete the smoke-test hop.
    case 'takeoff':
      return !airborne;

    // D40, the operator's answer to open question F on 2026-09-05: LAND joins
    // the emergency group the moment the aircraft is airborne, and leaves when
    // it is on the ground - where it is the one emergency verb with nothing to
    // act on.
    case 'land':
    // A descent to confirm requires a descent. Vendor support is a SEPARATE
    // question and stays a greyed refusal per D13: `availability.unsupported`
    // carries "Landing confirmation is DJI-only" on a MAVLink aircraft, and
    // that sentence is how an operator finds out.
    //
    // These two are the reason D45 exists. On `!airborne` they came off the bar
    // for every MAVLink aircraft below 2 m - including one held at ~1 m by DJI
    // landing protection, which is the exact state CONFIRM LANDING exists to
    // resolve, while `landingStateView` was still drawing a chip whose hover
    // text says "CONFIRM LANDING overrides that".
    case 'confirm_landing':
    // Fly-here repositions an aircraft that is already flying.
    case 'goto':
      return !onGround;

    // D38. On the ground the answer is certain and structural, so both keys go.
    // Airborne they STAY, greying themselves with "No mission running to hold."
    // when nothing is bound - because `onMission` is
    // `(selectedDroneMission?.waypoints?.length ?? 0) > 0`, resolved from a list
    // that is empty while its fetch is in flight, so it cannot tell "no mission"
    // from "not loaded yet". Filtering on it would drop both keys on every cold
    // load and put them back when the fetch lands, which is the moving key set
    // D14 exists to prevent.
    case 'pause':
    case 'resume':
      return !onGround;

    // ARM and DISARM are NOT filtered (D46, superseding D37).
    //
    // D37 removed each on positive evidence of the other, and noted that this
    // changes nothing for DJI because the companion keeps `_armed` on-device.
    // That was the whole problem: it buys nothing on the fleet the golden flow
    // uses, and on MAVLink - the one route that does publish `armed` - it costs
    // the launch sequence. ARM is the FIRST key and DISARM the LAST, so arming
    // removes one from one end and adds one at the other, and the right-anchored
    // key group re-lays out between the two presses of ARM then TAKEOFF. A key
    // that moves between the presses of the sequence it belongs to is the exact
    // thing D14 protects the emergency group from.
    //
    // Falls through to the default, so both render in every phase, as they do
    // today. D41's flag stands and is now moot for this PR: DISARM airborne is
    // exactly as it was.

    // Fails open per D34: a verb this function has not been taught about
    // renders, rather than disappearing the first time the bar learns a new one.
    default:
      return true;
  }
}

/** `keys.filter(...)` with the phase inputs bound once. */
export function visibleCommandKeys<T extends { action: BarKeyAction }>(
  keys: readonly T[],
  inputs: CommandKeyPhaseInputs,
): T[] {
  return keys.filter((k) => isCommandKeyVisible(k.action, inputs));
}
